import { c as _c } from "react/compiler-runtime";
import { KeyboardEvent, useCallback, useContext, useEffect, useReducer } from 'react';
import { findLastRecursive, findRecursive, mapRecursive } from './util';
import { Organisasjon } from './Virksomhetsvelger';
import { VirksomhetsvelgerContext } from './VirksomhetsvelgerContext';
export interface OrganisasjonMedState extends Organisasjon {
  index: number;
  fokusert: boolean;
  valgt: boolean;
  ekspandert: boolean;
  underenheter: OrganisasjonMedState[];
}
type Action = {
  type: 'FOKUSER_ENHET';
  payload: Organisasjon;
} | {
  type: 'FOKUSER_FØRSTE_ENHET';
} | {
  type: 'FOKUSER_SISTE_ENHET';
} | {
  type: 'TOGGLE_EKSPANDER_ENHET';
  payload: Organisasjon;
} | {
  type: 'RESET_STATE';
  payload: {
    aktivtOrganisasjonstre: Organisasjon[];
    valgtOrgNr: string;
  };
};
const reducer = (state: OrganisasjonMedState[], action: Action): OrganisasjonMedState[] => {
  let newState: OrganisasjonMedState[];
  switch (action.type) {
    case 'FOKUSER_ENHET':
      newState = mapRecursive(state, org => ({
        ...org,
        fokusert: org.orgnr === action.payload.orgnr
      }));
      break;
    case 'FOKUSER_FØRSTE_ENHET':
      newState = mapRecursive(state, org => ({
        ...org,
        fokusert: org.orgnr === state[0].orgnr
      }));
      break;
    case 'FOKUSER_SISTE_ENHET':
      newState = mapRecursive(state, org => ({
        ...org,
        fokusert: org.orgnr === (state[state.length - 1].ekspandert ? state[state.length - 1].underenheter[state[state.length - 1].underenheter.length - 1].orgnr : state[state.length - 1].orgnr)
      }));
      break;
    case 'TOGGLE_EKSPANDER_ENHET':
      newState = mapRecursive(state, org => ({
        ...org,
        ekspandert: org.orgnr === action.payload.orgnr ? !org.ekspandert : org.ekspandert,
        underenheter: org.underenheter.map(underenhet => ({
          ...underenhet,
          ekspandert: org.orgnr === action.payload.orgnr ? !org.ekspandert : org.ekspandert
        }))
      }));
      break;
    case 'RESET_STATE':
      newState = initState(action.payload);
      break;
  }
  return newState;
};
const initState = ({
  aktivtOrganisasjonstre,
  valgtOrgNr
}: {
  aktivtOrganisasjonstre: Organisasjon[];
  valgtOrgNr: string;
}): OrganisasjonMedState[] => {
  return mapRecursive(aktivtOrganisasjonstre, org => {
    // enheter uten underenheter har ingenting å ekspandere, men må være nåbare med tastatur
    const ekspandert = org.underenheter.length === 0 || org.underenheter.some(o => o.orgnr === valgtOrgNr);
    return {
      ...org,
      index: -1,
      fokusert: org.orgnr === valgtOrgNr,
      valgt: org.orgnr === valgtOrgNr,
      ekspandert: ekspandert,
      underenheter: org.underenheter.map(u => ({
        ...u,
        ekspandert: ekspandert,
        fokusert: u.orgnr === valgtOrgNr,
        valgt: u.orgnr === valgtOrgNr
      })) as OrganisasjonMedState[]
    };
  });
};
type UseTastaturNavigasjon = {
  fokusertEnhet: OrganisasjonMedState;
  organisasjonerMedState: OrganisasjonMedState[];
  fokuserFørsteEnhet: () => void;
  fokuserSisteEnhet: () => void;
  pilOpp: () => void;
  pilNed: () => void;
  pilHøyre: () => void;
  pilVenstre: () => void;
  toggleEkspander: (enhet: Organisasjon) => void;
  fokuserEnhet: (enhet: Organisasjon) => void;
  resetState: () => void;
  handleKeyDown: (e: KeyboardEvent) => void;
};
export const useTastaturNavigasjon = () => {
  const $ = _c(49);
  const {
    aktivtOrganisasjonstre,
    valgtOrganisasjon
  } = useContext(VirksomhetsvelgerContext);
  let t0;
  if ($[0] !== aktivtOrganisasjonstre || $[1] !== valgtOrganisasjon.orgnr) {
    t0 = {
      aktivtOrganisasjonstre,
      valgtOrgNr: valgtOrganisasjon.orgnr
    };
    $[0] = aktivtOrganisasjonstre;
    $[1] = valgtOrganisasjon.orgnr;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [organisasjonerMedState, dispatch] = useReducer(reducer, t0, initState);
  let t1;
  if ($[3] !== aktivtOrganisasjonstre || $[4] !== valgtOrganisasjon.orgnr) {
    t1 = () => {
      dispatch({
        type: "RESET_STATE",
        payload: {
          aktivtOrganisasjonstre,
          valgtOrgNr: valgtOrganisasjon.orgnr
        }
      });
    };
    $[3] = aktivtOrganisasjonstre;
    $[4] = valgtOrganisasjon.orgnr;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const resetState = t1;
  let t2;
  if ($[6] !== aktivtOrganisasjonstre) {
    t2 = JSON.stringify(aktivtOrganisasjonstre);
    $[6] = aktivtOrganisasjonstre;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const aktivtOrganisasjonstreJson = t2;
  let t3;
  if ($[8] !== resetState) {
    t3 = () => {
      resetState();
    };
    $[8] = resetState;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== aktivtOrganisasjonstreJson || $[11] !== resetState || $[12] !== valgtOrganisasjon.orgnr) {
    t4 = [aktivtOrganisasjonstreJson, valgtOrganisasjon.orgnr, resetState];
    $[10] = aktivtOrganisasjonstreJson;
    $[11] = resetState;
    $[12] = valgtOrganisasjon.orgnr;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  useEffect(t3, t4);
  let t5;
  if ($[14] !== organisasjonerMedState || $[15] !== valgtOrganisasjon.orgnr) {
    t5 = findRecursive(organisasjonerMedState, _temp) ?? findRecursive(organisasjonerMedState, t6 => {
      const {
        orgnr
      } = t6;
      return valgtOrganisasjon.orgnr === orgnr;
    });
    $[14] = organisasjonerMedState;
    $[15] = valgtOrganisasjon.orgnr;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const fokusertEnhet = t5;
  let t6;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      dispatch({
        type: "FOKUSER_F\xD8RSTE_ENHET"
      });
    };
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const fokuserFørsteEnhet = t6;
  let t7;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      dispatch({
        type: "FOKUSER_SISTE_ENHET"
      });
    };
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const fokuserSisteEnhet = t7;
  let t8;
  if ($[19] !== fokusertEnhet || $[20] !== organisasjonerMedState) {
    t8 = () => {
      const nextEnhet = findLastRecursive(organisasjonerMedState, t9 => {
        const {
          index,
          ekspandert,
          underenheter
        } = t9;
        if (underenheter.length === 0) {
          return index < fokusertEnhet.index && ekspandert;
        } else {
          return index < fokusertEnhet.index;
        }
      });
      if (nextEnhet !== undefined) {
        dispatch({
          type: "FOKUSER_ENHET",
          payload: nextEnhet
        });
      }
    };
    $[19] = fokusertEnhet;
    $[20] = organisasjonerMedState;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const pilOpp = t8;
  let t9;
  if ($[22] !== fokusertEnhet || $[23] !== organisasjonerMedState) {
    t9 = () => {
      const nextEnhet_0 = findRecursive(organisasjonerMedState, t10 => {
        const {
          index: index_0,
          ekspandert: ekspandert_0,
          underenheter: underenheter_0
        } = t10;
        if (underenheter_0.length === 0) {
          return index_0 > fokusertEnhet.index && ekspandert_0;
        } else {
          return index_0 > fokusertEnhet.index;
        }
      });
      if (nextEnhet_0 !== undefined) {
        dispatch({
          type: "FOKUSER_ENHET",
          payload: nextEnhet_0
        });
      }
    };
    $[22] = fokusertEnhet;
    $[23] = organisasjonerMedState;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const pilNed = t9;
  let t10;
  if ($[25] !== fokusertEnhet || $[26] !== pilNed) {
    t10 = () => {
      if (fokusertEnhet.underenheter.length > 0) {
        if (fokusertEnhet.ekspandert) {
          pilNed();
        } else {
          dispatch({
            type: "TOGGLE_EKSPANDER_ENHET",
            payload: fokusertEnhet
          });
        }
      }
    };
    $[25] = fokusertEnhet;
    $[26] = pilNed;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const pilHøyre = t10;
  let fokuserEnhet;
  let pilVenstre;
  let toggleEkspander;
  if ($[28] !== fokusertEnhet || $[29] !== organisasjonerMedState) {
    pilVenstre = () => {
      if (fokusertEnhet.underenheter.length > 0) {
        if (fokusertEnhet.ekspandert) {
          toggleEkspander(fokusertEnhet);
        }
      } else {
        const nextEnhet_1 = findLastRecursive(organisasjonerMedState, t11 => {
          const {
            index: index_1,
            ekspandert: ekspandert_1,
            underenheter: underenheter_1
          } = t11;
          return index_1 < fokusertEnhet.index && ekspandert_1 && underenheter_1.some(u => u.orgnr === fokusertEnhet.orgnr);
        });
        if (nextEnhet_1 !== undefined) {
          fokuserEnhet(nextEnhet_1);
        }
      }
    };
    toggleEkspander = enhet => {
      dispatch({
        type: "TOGGLE_EKSPANDER_ENHET",
        payload: enhet
      });
    };
    fokuserEnhet = enhet_0 => {
      dispatch({
        type: "FOKUSER_ENHET",
        payload: enhet_0
      });
    };
    $[28] = fokusertEnhet;
    $[29] = organisasjonerMedState;
    $[30] = fokuserEnhet;
    $[31] = pilVenstre;
    $[32] = toggleEkspander;
  } else {
    fokuserEnhet = $[30];
    pilVenstre = $[31];
    toggleEkspander = $[32];
  }
  let t11;
  if ($[33] !== pilHøyre || $[34] !== pilNed || $[35] !== pilOpp || $[36] !== pilVenstre) {
    t11 = e => {
      if (e.key === "Home") {
        fokuserFørsteEnhet();
        e.preventDefault();
      }
      if (e.key === "End") {
        fokuserSisteEnhet();
        e.preventDefault();
      }
      if (e.key === "ArrowUp" || e.key === "Up") {
        pilOpp();
        e.preventDefault();
      }
      if (e.key === "ArrowDown" || e.key === "Down") {
        pilNed();
        e.preventDefault();
      }
      if (e.key === "ArrowRight" || e.key === "Right") {
        pilHøyre();
        e.preventDefault();
      }
      if (e.key === "ArrowLeft" || e.key === "Left") {
        pilVenstre();
        e.preventDefault();
      }
    };
    $[33] = pilHøyre;
    $[34] = pilNed;
    $[35] = pilOpp;
    $[36] = pilVenstre;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  const handleKeyDown = t11;
  let t12;
  if ($[38] !== fokuserEnhet || $[39] !== fokusertEnhet || $[40] !== handleKeyDown || $[41] !== organisasjonerMedState || $[42] !== pilHøyre || $[43] !== pilNed || $[44] !== pilOpp || $[45] !== pilVenstre || $[46] !== resetState || $[47] !== toggleEkspander) {
    t12 = {
      fokusertEnhet,
      organisasjonerMedState,
      fokuserFørsteEnhet,
      fokuserSisteEnhet,
      pilOpp,
      pilNed,
      pilHøyre,
      pilVenstre,
      toggleEkspander,
      fokuserEnhet,
      resetState,
      handleKeyDown
    };
    $[38] = fokuserEnhet;
    $[39] = fokusertEnhet;
    $[40] = handleKeyDown;
    $[41] = organisasjonerMedState;
    $[42] = pilHøyre;
    $[43] = pilNed;
    $[44] = pilOpp;
    $[45] = pilVenstre;
    $[46] = resetState;
    $[47] = toggleEkspander;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  return t12;
};
function _temp(t0) {
  const {
    fokusert
  } = t0;
  return fokusert;
}